import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { CSSProperties, ReactNode } from 'react';

import {
  CardQuality,
  InGameCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { Bezel } from '@sorare/core/src/atoms/ui/Bezel';
import { FragmentOf, unmaskFragment } from '@sorare/core/src/lib/graphql';

import type { Shape_probabilisticBundleSlotConfig } from './__generated__/index.graphql';

type Props = {
  config: FragmentOf<Shape_probabilisticBundleSlotConfig>;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
};

export const Shape = ({
  config: config_,
  children,
  className,
  style,
}: Props) => {
  const config = unmaskFragment(config_);
  let color: string = 'var(--c-nd-500)';

  // jersey
  // ticket
  if (config.deliverableItems?.length) {
    color = 'var(--c-yellow-700)';
  }

  // card
  if (config.cards?.length) {
    const card = config.cards[0];
    color = card.quality
      ? (
          {
            [CardQuality.TIER_0]: 'var(--c-yellow-700)',
            [CardQuality.TIER_1]: 'var(--c-blue-400)',
            [CardQuality.TIER_2]: 'var(--c-green-500)',
            [CardQuality.TIER_3]: 'var(--c-green-500)',
            [CardQuality.TIER_4]: 'var(--c-green-500)',
            [CardQuality.TIER_5]: 'var(--c-green-500)',
          } as const
        )[card.quality]
      : 'var(--c-green-500)';
  }

  // XP
  if (config.inGameCurrencies?.length) {
    const igc = config.inGameCurrencies.filter(
      x => x.currency !== InGameCurrency.GOLDEN_WHEEL_TICKET
    )[0];
    // XP
    if (igc?.currency === InGameCurrency.LIMITED_XP && igc.amount > 350) {
      color = 'var(--c-green-500)';
    }
  }
  // conversion credit
  if (config.conversionCredit) {
    color =
      config.conversionCredit.percentageDiscount > 0.2
        ? 'var(--c-green-500)'
        : 'var(--c-nd-500)';
  }

  return (
    <Bezel
      background={`linear-gradient(
      to bottom,
      transparent calc(100% - 10px),
      ${color} 10px
    ),
    radial-gradient(var(--c-nl-50) 0%, var(--c-nl-900) 100%),
    linear-gradient(var(--c-nl-900) 0%, var(--c-nl-50) 50%), ${color}`}
      style={{ ...style, padding: '0 0 var(--unit)' }}
      className={className}
    >
      {children}
    </Bezel>
  );
};

Shape.fragments = {
  ProbabilisticBundleSlotConfig: gql`
    fragment Shape_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      probability

      deliverableItems {
        quantity
      }
      cards {
        quality
      }
      conversionCredit {
        id
        percentageDiscount
      }
      inGameCurrencies {
        id
        currency
        amount
      }
    }
  ` as TypedDocumentNode<Shape_probabilisticBundleSlotConfig>,
};
